/* eslint-disable @typescript-eslint/no-explicit-any */
import { executeApiCall } from 'services/api';
import { getMutations } from 'entities/hooks/mutation';
import { SuccessData } from 'types/api';
import { ClosureFormData, Demographics, ExtraInformation, FullProject, Project } from '../types';
import { uploadMutations } from 'entities/modules/uploads';

type UpdateProjectPropertyParams = {
  justification: string;
  projectId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  properties: Record<string, any>;
};

type FinishProjectParams = {
  project: string;

  justification?: string;

  closureForm: Pick<
    ClosureFormData,
    | 'comment'
    | 'canceledTasksPriceCheckControl'
    | 'emailChainAddition'
    | 'outsourceEmailControl'
    | 'poNumber'
    | 'servicesActualMetrics'
  >;
};

type ProjectTypeConfig = Pick<Project, 'waves' | 'type' | 'shadowBanInWaves' | 'originProject'>;

export type CreateProjectParams = Pick<
  Project,
  | 'name'
  | 'client'
  | 'clientOrigin'
  | 'ticket'
  | 'salesPerson'
  | 'services'
  | 'countries'
  | 'minFee'
  | 'teamResponsible'
  | 'estimatedCompletionWorkingDays'
  | 'deviceTarget'
  | 'currency'
> & {
  demographics: Record<string, Demographics>;
  observations: ExtraInformation;
  readyToInvoice: boolean;
  typeConfig: ProjectTypeConfig;

  files: Array<{
    file: File;
    name: string;
  }>;
};

export const projectEntityMutations = getMutations({
  createProject: {
    action: async ({
      files: filesToUpload,
      ...rest
    }: CreateProjectParams): Promise<SuccessData> => {
      const { files } = await uploadMutations.multiFileUpload.action({
        files: filesToUpload.map((f) => f.file),
        prefix: 'projects',
      });

      return executeApiCall({
        path: '/projects',

        payload: {
          ...rest,

          files: files.map(({ name, type, ...file }, index) => ({
            ...file,
            name: filesToUpload[index].name,
            filename: name,
            contentType: type,
          })),
        },
      });
    },

    messages: {
      success: (t) => ({ title: t('project-created-with-success'), type: 'success' }),
    },

    parseError: ({ message }) => {
      const msgs = {
        'Missing required permissions': (t: any) => ({ title: t('missing-required-permissions') }),
        'Team referenced does not have permission to own project': (t: any) => ({
          title: t('team-referenced-does-not-have-permission-to-own-project'),
        }),
        'Client not found': (t: any) => ({ title: t('client-not-found') }),
        'Ticket not found': (t: any) => ({ title: t('ticket-not-found') }),
        'Country referenced mismatch': (t: any) => ({ title: t('country-referenced-mismatch') }),
      };

      return (
        msgs[message as keyof typeof msgs] ??
        ((t: any) => ({
          title: t('error-while-creating-project'),
          type: 'error',
        }))
      );
    },
  },

  updateProjectProperty: {
    action: async ({
      properties,
      projectId,
      ...params
    }: UpdateProjectPropertyParams): Promise<SuccessData> => {
      return executeApiCall({
        path: '/projects',

        method: 'put',

        payload: {
          ...params,
          id: projectId,
          ...properties,
        },
      });
    },
  },

  changeProjectStatus: {
    action: async (payload: {
      project: string;
      status: string;
      justification?: string;
    }): Promise<SuccessData> => {
      return executeApiCall({
        path: '/projects/status',

        method: 'put',

        payload,
      });
    },
    messages: {
      error: (t) => ({
        title: t('could-not-change-project-status'),
        description: t('try-again-in-a-few-moments') as string,
      }),
    },
  },

  assignProject: {
    action: async (payload: { id: string; owner: string }): Promise<FullProject> => {
      return executeApiCall({
        path: '/projects/assume',

        payload,
      });
    },

    messages: {
      success: (t) => t('project-assigned-with-success')!,

      error: (t) => t('error-while-assigning-project')!,
    },
  },

  finishProject: {
    action: async (payload: FinishProjectParams): Promise<SuccessData> => {
      return executeApiCall({
        path: '/projects/finish',

        payload,
      });
    },
  },

  authorizeProjectStart: {
    action: async (payload: { id: string }): Promise<SuccessData> => {
      return executeApiCall({
        path: '/projects/authorize-start',

        payload,
      });
    },

    messages: {
      suppressSuccess: true,
    },
  },

  startNewTrackerWave: {
    action: async (payload: { origin: string; name: string }): Promise<{ id: string }> => {
      return executeApiCall({
        path: 'projects/tracker',

        payload,
      });
    },

    messages: {
      success: (t) => ({
        title: t('wave-created-with-success'),
        description: t('please-define-expected-dates-to-start-project') as string,
        type: 'success',
      }),

      error: (t) => ({
        title: t('something-went-wrong-when-starting-wave'),
        type: 'error',
      }),
    },
  },

  startProject: {
    action: async (payload: {
      id: string;
      taskDates: Record<string, string>;
    }): Promise<SuccessData> => {
      return executeApiCall({
        path: '/projects/start',

        payload,
      });
    },

    messages: {
      suppressSuccess: true,
      error: (t) => ({
        title: t('could-not-start-project'),
        type: 'error',
      }),
    },
  },

  reactivateProject: {
    action: async (payload: { project: string; justification: string }): Promise<SuccessData> => {
      return executeApiCall({
        path: '/projects/reactivate',

        payload,
      });
    },

    messages: {
      success: (t) => ({
        title: t('project-reactivated-with-success'),
        description: t('task-owners-were-be-notified')!,
        type: 'success',
      }),

      error: (t) => ({
        title: t('project-reactivation-failed'),
        type: 'error',
      }),
    },
  },

  changeProjectOwner: {
    action: async (payload: {
      project: string;
      justification: string;
      owner: string;
    }): Promise<SuccessData> => {
      return executeApiCall({
        path: '/projects/owner',
        method: 'put',

        payload,
      });
    },

    messages: {
      success: (t) => ({
        title: t('project-ownership-changed'),
        type: 'success',
      }),

      error: (t) => ({
        title: t('project-ownership-error'),
        type: 'error',
      }),
    },
  },

  cancelProject: {
    action: async (payload: { project: string; justification: string }): Promise<SuccessData> => {
      return executeApiCall({
        path: '/projects/cancel',

        payload,
      });
    },

    messages: {
      success: (t) => ({
        title: t('project-canceled-successfully'),
        type: 'success',
      }),

      error: (t) => ({
        title: t('project-cancellation-failed'),
        type: 'error',
      }),
    },
  },
});
